import { useEffect, useState } from "react";
import { Loader2, Link2, TrendingUp, Globe, Award, ExternalLink, AlertCircle, Search } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { toast } from "sonner";

export default function BacklinksPage() {
  const { active } = useProjects();
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (active?.domain && !domain) {
      try {
        const u = active.domain.startsWith("http") ? new URL(active.domain).hostname : active.domain;
        setDomain(u.replace(/^www\./, ""));
      } catch {}
    }
  }, [active?.domain, domain]);

  const run = async (e) => {
    e?.preventDefault();
    if (!domain) { toast.error("Enter a domain"); return; }
    setLoading(true);
    setError(null);
    setSummary(null);
    setList(null);
    try {
      const [s, l] = await Promise.all([
        api.post("/backlinks/summary", { domain }),
        api.post("/backlinks/list", { domain, limit: 50 }),
      ]);
      setSummary(s.data.summary);
      setList(l.data.items || []);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || formatApiError(e2);
      setError(msg);
      toast.error(e2?.response?.status === 402 ? "Backlinks API subscription required" : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      eyebrow="Backlinks"
      title="Real backlink data — see who links to you and your competitors."
      subtitle="Live data via DataForSEO Backlinks API. Domain rank, referring domains, top anchors, full backlink list."
    >
      <form onSubmit={run} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-6" data-testid="backlinks-form">
        <div className="grid md:grid-cols-[1fr_180px] gap-3 items-end">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Domain</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. yoursite.com or competitor.com"
                   className="h-12 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="backlinks-domain" />
          </div>
          <Button type="submit" disabled={loading} className="h-12 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="backlinks-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Analyze</>}
          </Button>
        </div>
      </form>

      {error && (
        <div className="bg-amber-950/30 border border-amber-700/40 rounded-sm p-5 mb-6 flex items-start gap-3" data-testid="backlinks-error">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200 leading-relaxed">{error}</div>
        </div>
      )}

      {loading && <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-16 text-center"><Loader2 className="w-7 h-7 animate-spin text-[#0055FF] mx-auto mb-3" /><p className="text-sm text-zinc-400">Pulling fresh backlink data…</p></div>}

      {summary && (
        <div className="grid sm:grid-cols-4 gap-3 mb-6" data-testid="backlinks-kpis">
          <Kpi icon={Link2} label="Total backlinks" value={summary.total_backlinks?.toLocaleString()} color="#3377FF" />
          <Kpi icon={Globe} label="Referring domains" value={summary.referring_domains?.toLocaleString()} color="#10B981" />
          <Kpi icon={Award} label="Domain rank" value={summary.rank} color="#F59E0B" />
          <Kpi icon={TrendingUp} label="Dofollow" value={summary.dofollow_backlinks?.toLocaleString()} color="#A855F7" />
        </div>
      )}

      {(summary || list) && (
        <Tabs defaultValue="list" className="space-y-4">
          <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm">
            <TabsTrigger value="list" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="bl-tab-list">
              Top backlinks ({list?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="anchors" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="bl-tab-anchors">Top anchors</TabsTrigger>
            <TabsTrigger value="referring" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="bl-tab-ref">Top referring</TabsTrigger>
          </TabsList>

          <TabsContent value="list">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                      <th className="px-4 py-3 font-normal">Source domain</th>
                      <th className="px-4 py-3 font-normal">Anchor text</th>
                      <th className="px-4 py-3 font-normal text-right">Rank</th>
                      <th className="px-4 py-3 font-normal text-center">Type</th>
                      <th className="px-4 py-3 font-normal"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(list || []).map((b, i) => (
                      <tr key={i} className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50">
                        <td className="px-4 py-2.5 truncate max-w-[280px]">
                          <div className="font-mono text-zinc-200 truncate" title={b.url_from}>{b.domain_from}</div>
                        </td>
                        <td className="px-4 py-2.5 truncate max-w-[280px] text-zinc-300" title={b.anchor}>{b.anchor || <em className="text-zinc-600">(no anchor)</em>}</td>
                        <td className="px-4 py-2.5 text-right text-zinc-400 font-mono">{b.rank}</td>
                        <td className="px-4 py-2.5 text-center">
                          {b.dofollow
                            ? <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm text-[10px]">DOFOLLOW</Badge>
                            : <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">NOFOLLOW</Badge>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <a href={b.url_from} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[#3377FF]"><ExternalLink className="w-3.5 h-3.5" /></a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!list?.length && <div className="p-12 text-center text-zinc-500 text-sm">No backlinks returned.</div>}
            </div>
          </TabsContent>

          <TabsContent value="anchors">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
              <div className="flex flex-wrap gap-1.5" data-testid="bl-anchors">
                {(summary?.anchors_top || []).map((a, i) => (
                  <Badge key={i} variant="outline" className="rounded-sm border-zinc-700 text-zinc-300 text-xs">
                    {a.anchor || "(no anchor)"} <span className="text-zinc-500 ml-1.5">{a.backlinks?.toLocaleString()}</span>
                  </Badge>
                ))}
                {!(summary?.anchors_top || []).length && <p className="text-sm text-zinc-500">No anchor data returned.</p>}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="referring">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
              <ul className="space-y-2" data-testid="bl-referring">
                {(summary?.referring_domains_top || []).map((r, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2">
                    <Globe className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="flex-1 font-mono text-zinc-200">{r.domain || r.referring_domain}</span>
                    <span className="text-zinc-500 text-xs">{r.backlinks?.toLocaleString()}</span>
                  </li>
                ))}
                {!(summary?.referring_domains_top || []).length && <p className="text-sm text-zinc-500">No referring-domain data returned.</p>}
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {!loading && !summary && !error && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-500">
          <Link2 className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
          <p>Enter any domain to see who links to it.</p>
          <p className="text-xs mt-2 text-zinc-600">Requires an active DataForSEO Backlinks API subscription (separate from Keywords).</p>
        </div>
      )}
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-sm grid place-items-center flex-shrink-0" style={{ background: `${color}15`, border: `1px solid ${color}40` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <div className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
        <div className="font-display text-xl font-semibold">{value ?? "—"}</div>
      </div>
    </div>
  );
}
